import { useCallback } from 'react';

import { getAxisLabel } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED } from 'utils/mixpanelEventNames';
import type { TableRow } from './packArchitectureTable.helpers';

type UsePackArchitectureTableRowInteractionsParams = {
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    selectSegment: (segmentKey: string) => void;
    clearSelection: () => void;
    submitTableSearch: (override?: string) => void;
};

export function usePackArchitectureTableRowInteractions({
    activeSegmentKey,
    selectedColumnKey,
    selectSegment,
    clearSelection,
    submitTableSearch,
}: UsePackArchitectureTableRowInteractionsParams) {
    const {
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
    } = usePackArchitectureContext();

    const getRowClassName = useCallback(
        (record: TableRow) => {
            if (!activeSegmentKey) return 'table-row-clickable';
            if (record.key === activeSegmentKey) return 'table-row-selected table-row-clickable';
            return 'table-row-dimmed table-row-clickable';
        },
        [activeSegmentKey],
    );

    const handleRowClick = useCallback(
        (record: TableRow) => {
            const isDeselect = activeSegmentKey === record.key && !selectedColumnKey;
            selectSegment(record.key);
            if (isDeselect) return;

            trackEventMixpanel(PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED, {
                selection_source: 'table',
                bubble_represents: getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
                bubble_represents_value: record.label,
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
            });
        },
        [
            activeSegmentKey,
            bubbleRepresents,
            bubbleRepresentsOptionGroups,
            selectSegment,
            selectedColumnKey,
            xAxis,
            xAxisOptionGroups,
            yAxis,
            yAxisOptionGroups,
        ],
    );

    const clearHighlight = useCallback(() => {
        clearSelection();
    }, [clearSelection]);

    const handleSubmitSearch = useCallback(
        (override?: string) => {
            submitTableSearch(override);
        },
        [submitTableSearch],
    );

    return {
        getRowClassName,
        handleRowClick,
        clearHighlight,
        handleSubmitSearch,
    };
}
